import appConfig from '@/constants/appConfig'
import errorCodes from '@/constants/errorCodes'
import { getAuthHeaders } from '@/helpers/apiHelper'
import AppService from '@/services/appService'

class AiApiClient {
  private readonly baseUrl: string
  private appService

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
    this.appService = new AppService()
  }

  async fetchWithRetry(
    url: string,
    options: RequestInit,
    isContentTypeNotRequired = false,
    retryCount = 1,
    fromautofix = false
  ): Promise<any> {
    const response = await fetch(this.baseUrl + url, options)
    if (response.status === 204 && fromautofix == true) {
      return {
        status: 204,
        message: "No products present. Autofix not possible."
      };
    }
    if (response.status === 422 && fromautofix == true) {
      return {
        status: 500,
        message: response.statusText
      };
    }

    let responseData: any = null
    try {
      responseData = await response.json()
    } catch {
      responseData = null
    }

    if ((response.status === 401 || response.status === 403) && retryCount > 0) {
      const isTokenError =
        responseData?.detail?.errorCode == errorCodes.TokenExpiredException ||
        responseData?.detail?.errorCode == errorCodes.TokenInvalidException ||
        responseData?.detail?.errorCode == errorCodes.TokenMalformedException ||
        responseData?.detail === 'Not authenticated' ||
        responseData?.errorCode == errorCodes.Unauthorized ||
        response.status === 401 ||
        response.status === 403

      if (isTokenError) {
        const forceRefresh = responseData?.detail?.errorCode != errorCodes.TokenExpiredException
        const refreshed = await this.regenerateToken(forceRefresh)

        if (refreshed) {
          options.headers = getAuthHeaders(isContentTypeNotRequired)
          return this.fetchWithRetry(url, options, isContentTypeNotRequired, retryCount - 1, fromautofix)
        }
      }
    }

    if (!response.ok) {
      throw new Error(response.statusText)
    }

    return responseData
  }

  async fetchApi(
    url: string,
    options: RequestInit,
    isContentTypeNotRequired = false,
    retryCount = 1,
  ): Promise<any> {
    await this.regenerateToken()

    options.headers = getAuthHeaders(isContentTypeNotRequired)
    return this.fetchWithRetry(url, options, isContentTypeNotRequired, retryCount - 1)
  }

  private async regenerateToken(forceRefresh = false): Promise<boolean> {
    const { restoreSession } = await import('@/api/idp')
    const success = await restoreSession(forceRefresh)

    if (!success) {
      await this.appService.recoverSession()
      return false
    }

    return true
  }
}

export default AiApiClient
